import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody} from "@material-tailwind/react";
import {API} from "../../../types/API";
import {AxiosResponse} from "axios";
import {Input} from "@material-tailwind/react/components/Input";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../store";
import {getError, hasError} from "../../../hooks/errors";
import {clear, validateData} from "../../../reducers/validationReducer";
import {InputType} from "../../../types/InputType";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useNavigate, useParams} from "react-router-dom";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Country} from "../../../types/Responses/CountriesResponse";
import {Area} from "react-easy-crop";
import {Size} from "react-easy-crop/types";
import CropImg from "../../../components/CropImage/CropImg";
import {Shape} from "../../../components/CropImage/types/Shape";
import onUpload from "../../../components/CropImage/helpers/onUpload";
import {getCroppedImage} from "../../../components/CropImage/helpers/getCroppedImage";
import {ImageSize} from "../../../components/CropImage/types/ImageSize";
import FormSkeleton from "../../../components/Skeletons/FormSkeleton";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useErrorHandler} from "react-error-boundary";
import Forbidden from "../../../components/Forbidden";
import {useUserContext} from "../../../types/contexts/UserContext";
import {useTranslation} from "react-i18next";
import Card from "../../../components/Card";
import {setTitle} from "../../../hooks/setTitle";
import {imageToBytes} from "../../../components/CropImage/helpers/imageToBytes";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import {removeDataTableRequest} from "../../../reducers/dataTableSearch";
import {setSidebarTitle} from "../../../reducers/sidebar";

const CountryForm = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    const navigate = useNavigate();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const user = useUserContext();
    const [showCropper, setShowCropper] = useState<boolean>(false);
    const [name, setName] = useState<string>();
    const [code, setCode] = useState<string>();
    const [phone, setPhone] = useState<string>();
    const [uploadedFile, setUploadedFile] = React.useState<string>('');
    const hiddenFileInput = React.useRef<HTMLInputElement>(null);
    const [cropped, setCropped] = React.useState<Area>();
    const [cropImageArea, setCropImageArea] = React.useState<HTMLImageElement>();
    const [isInProgress, setInProgress] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean>(false);
    const [formIsReady, setFormIsReady] = useState<boolean>(false);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const [withCrop, setWithCrop] = useState<boolean>(true);
    const dispatch = useDispatch();
    const errors = useSelector((state: RootState) => state.validation.errors);

    const handleClick = useCallback(() => {
        // @ts-expect-error can be undefined
        hiddenFileInput.current.click();
    }, [hiddenFileInput]);

    const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const {files} = event.target;
        if (files !== null) {
            try {
                onUpload(files[0], setUploadedFile);
                setShowCropper(true);
            } catch (e) {
                // @ts-expect-error can be undefined
                toast?.show(e.message)
            }
        }
        // @ts-expect-error can be undefined
        hiddenFileInput.current.value = '';
    }, [hiddenFileInput, toast, setShowCropper, setUploadedFile]);

    const onCroppedImage = useCallback((_crop: Area, percentCrop: Area, defaultSize: Size | undefined) => {
        if (defaultSize && (percentCrop.width === 0 || percentCrop.height === 0)) {
            percentCrop = {x: 0, y: 0, ...defaultSize};
        }
        setCropped(percentCrop);
    }, [setCropped]);

    const changeName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setName(e.currentTarget.value)
        }
    }, [setValidated, setName]);

    const changeCode = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setCode(e.currentTarget.value)
        }
    }, [setValidated, setCode]);

    const changePhone = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setPhone(e.currentTarget.value)
        }
    }, [setValidated, setPhone]);

    const handleSuccessResponse = useCallback((response: AxiosResponse, message: JSX.Element | string) => {
        const country = response.data.data as Country
        if (country) {
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {message}</>, "bg-teal-500");
            navigate('/app/admin/countries');
        }
    }, [toast, navigate]);

    const callback = useCallback((b: boolean) => {
        setValidated(true);
        setInProgress(false);

        if (showCropper && (!cropImageArea || !cropped)) {
            toast?.show(t('admin.images.empty_error'), "bg-red-500");
            return;
        }

        if (b) {
            let request = {name, code: code?.toUpperCase(), phone};
            if (showCropper) {
                const iTemp = cropImageArea && cropped && withCrop ? getCroppedImage(cropImageArea, cropped, ImageSize.country) : (!withCrop ? imageToBytes(document.getElementById('non-cropped-image') as HTMLImageElement) : '');
                request = {...request, ['flag' as keyof typeof request]: iTemp};
            }
            if (id) {
                API.updateCountry(parseInt(id ?? ''), request).then((response: AxiosResponse) => handleSuccessResponse(response, t('admin.countries.updated.title', {name}))).catch(e => ErrorHandler(e, `put${APILink.GET_COUNTRY.replace('$country', id.toString())}`, toast, handleError));
            } else {
                API.createCountry(request).then((response: AxiosResponse) => {
                    dispatch(removeDataTableRequest('/app/admin/countries'));
                    handleSuccessResponse(response, t('admin.countries.created.title', {name}));
                }).catch(e => ErrorHandler(e, `post${APILink.GET_COUNTRIES}`, toast, handleError));
            }

        }
    }, [setValidated, setInProgress, code, phone, showCropper, withCrop, cropped, cropImageArea, id, handleSuccessResponse, toast, handleError, name]);

    const submit = useCallback(() => {
        if (showCropper && (!cropImageArea || !cropped)) {
            toast?.show(t('admin.images.empty_error'), "bg-red-500");
            return;
        }
        setInProgress(true);
        const inputs = [
            {key: 'name', label: 'Name', type: InputType.text, regex: '^.{3,}\\b$'},
            {key: 'code', label: 'Code', type: InputType.text, regex: '^.{2,2}$'},
            {key: 'phone', label: 'Phone code', type: InputType.text, regex: '^\\d{1,5}$'}
        ];
        let data = {name, code, phone};
        if (showCropper) {
            const iTemp = cropImageArea && cropped ? getCroppedImage(cropImageArea, cropped, ImageSize.country) : '';
            inputs.push({
                key: 'flag',
                label: t('admin.countries.table.flag'),
                type: InputType.text,
                regex: '^data:image/png;base64,.{32,}'
            });
            data = {...data, ['flag' as keyof typeof data]: iTemp};
        }
        dispatch(validateData({
            payload: {
                inputs: inputs,
                introducedData: data,
                callback: callback
            }
        }));
    }, [setInProgress, showCropper, cropped, cropImageArea, phone, dispatch, callback, code, name, toast]);

    const getSingleCountry = useCallback(() => {
        API.getCountry(parseInt(id ?? '')).then((response: AxiosResponse) => {
            const countryResponse = response.data.data as Country
            setName(countryResponse.name);
            setCode(countryResponse.code);
            setPhone(countryResponse.phone ?? '');
            setUploadedFile(`${window.location.origin}/images/countries/${countryResponse.flag}`);
            setShowCropper(false);
            setFormIsReady(true);
            setTitle(t('admin.buttons.update') + " " + countryResponse.name);
            dispatch(setSidebarTitle(t('admin.countries.update.title', {name: countryResponse.name})));
        }).catch((e) => {
            ErrorHandler(e, `get${APILink.GET_COUNTRY.replace('$country', id?.toString() ?? '')}`, toast, handleError, setIsForbidden);
            setFormIsReady(true);
        });
    }, [setName, setCode, setUploadedFile, setShowCropper, setFormIsReady, handleError, toast, id]);

    useEffect(() => {
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                if (id) {
                    getSingleCountry();
                } else {
                    setTitle(t('admin.buttons.create'));
                    dispatch(setSidebarTitle(t('admin.countries.create.title')));
                    if (user && !user.user.permissions.includes('create country'))
                        setIsForbidden(true);
                    setFormIsReady(true);
                    setShowCropper(true);
                }
            }).catch();
        }
        dispatch(clear())
    }, [dispatch, getSingleCountry, setSidebarTitle, id, t]);

    const uploadImageUI = useMemo(() => {
        return <>
            <div className="max-wmd mt-4">
                <Button className="bg-black text-white"
                        onClick={() => handleClick()}>{uploadedFile ? t('admin.images.replace') : t('admin.images.upload')}</Button>
                <input type="file" accept="image/*" ref={hiddenFileInput} onChange={handleChange}
                       style={{display: 'none'}}/>
                {validated && hasError('flag', errors) &&
                    <label key={'error-flag'}
                           className="mt-1 text-red-500 text-xs block">{getError('flag', errors)}</label>}
            </div>

            {showCropper && <div className="max-w-md mt-4 relative">
                <CropImg setWithCropCallback={setWithCrop} shape={Shape.RECTANGLE} ratio={2} img={uploadedFile}
                         onCropCompleted={onCroppedImage} onCropLoaded={(i) => setCropImageArea(i)} withoutLeft/>
            </div>}
            {!showCropper && uploadedFile &&
                <div className="max-w-md mt-4 flex"><img src={uploadedFile} className="h-36"/></div>}
        </>
    }, [uploadedFile, validated, errors, onCroppedImage, setCropImageArea, handleClick, handleChange, showCropper]);

    return (
        <PageBlock>
            {!formIsReady ? <FormSkeleton/> : <>
                <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                    <CardBody className="overflow-auto px-0">
                        {!isForbidden ? <>
                            <div className="max-w-md">
                                <Input success={!hasError('name', errors) && validated}
                                       error={validated && hasError('name', errors)} crossOrigin=''
                                       variant={'outlined'} label={t('admin.countries.table.name')} type={'text'}
                                       onChange={changeName}
                                       value={name}
                                       className="inline-block dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent"
                                       labelProps={{className: "dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800"}}/>
                                {validated && hasError('name', errors) &&
                                    <label key={'error-name'}
                                           className="mt-1 text-red-500 text-xs block">{getError('name', errors)}</label>}
                            </div>
                            <div className="max-w-md mt-4">
                                <Input success={!hasError('code', errors) && validated}
                                       error={validated && hasError('code', errors)} crossOrigin=''
                                       variant={'outlined'} label={t('admin.countries.table.code')} type={'text'}
                                       onChange={changeCode}
                                       value={code}
                                       className="inline-block dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent"
                                       labelProps={{className: "dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800"}}/>
                                {validated && hasError('code', errors) &&
                                    <label key={'error-code'}
                                           className="mt-1 text-red-500 text-xs block">{getError('code', errors)}</label>}
                            </div>
                            <div className="max-w-md mt-4">
                                <Input success={!hasError('phone', errors) && validated}
                                       maxLength={5}
                                       error={validated && hasError('phone', errors)} crossOrigin=''
                                       variant={'outlined'} label={t('admin.countries.table.phone')} type={'number'}
                                       onChange={changePhone}
                                       value={phone}
                                       className="inline-block dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent"
                                       labelProps={{className: "dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800"}}/>
                                {validated && hasError('phone', errors) &&
                                    <label key={'error-phone'}
                                           className="mt-1 text-red-500 text-xs block">{getError('phone', errors)}</label>}
                            </div>

                            {uploadImageUI}

                            <div className="mt-12">
                                <Button onClick={() => submit()} className="text-white bg-teal-500"
                                        disabled={isInProgress}>{id ? t('admin.buttons.update') : t('admin.buttons.create')}</Button>
                            </div>
                        </> : <Forbidden isForbidden={isForbidden}/>}
                    </CardBody>
                </Card>
            </>}
        </PageBlock>
    );
}
export default memo(CountryForm);
